/**
 * Turning cell regions into outlines (TD §7.2 "Trace the boundaries").
 *
 * traceRegions outlines every labelled region of a grid at once and emits a shared corner pool: each wall between
 * two regions is one chain of grid corners, simplified once and used by both regions (one forwards, one reversed),
 * and corners where three or more regions meet (counting "no region" as one) are junctions, to be pinned.
 * traceMask, dp and simplifyLoop are the prototype's single-mask helpers, used for pools.
 */
import type { Pt } from '../../shared/types';
import { distSeg, polyArea } from '../geom';

/** Outline of the largest loop of a mask, in cell-corner coordinates (the prototype's traceMask). */
export function traceMask(mask: Uint8Array, N: number): Pt[] {
  const E: [number, number, number, number][] = [], out = new Map<number, number[]>(), key = (x: number, y: number) => y * (N + 1) + x;
  const add = (x1: number, y1: number, x2: number, y2: number) => {
    const k = key(x1, y1);
    let l = out.get(k);
    if (!l) { l = []; out.set(k, l); }
    l.push(E.length);
    E.push([x1, y1, x2, y2]);
  };
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) if (mask[j * N + i]) {
    if (j === 0 || !mask[(j - 1) * N + i]) add(i, j, i + 1, j);
    if (i === N - 1 || !mask[j * N + i + 1]) add(i + 1, j, i + 1, j + 1);
    if (j === N - 1 || !mask[(j + 1) * N + i]) add(i + 1, j + 1, i, j + 1);
    if (i === 0 || !mask[j * N + i - 1]) add(i, j + 1, i, j);
  }
  const used = new Uint8Array(E.length);
  let best: Pt[] = [], bestA = -1;
  for (let e0 = 0; e0 < E.length; e0++) if (!used[e0]) {
    const loop: Pt[] = [];
    let e = e0;
    for (let guard = 0; guard < E.length + 1; guard++) {
      used[e] = 1;
      const ed = E[e] as [number, number, number, number];
      loop.push([ed[0], ed[1]]);
      const cand = out.get(key(ed[2], ed[3])) ?? [];
      let nx = -1;
      for (const ci of cand) if (!used[ci]) { nx = ci; break; }
      if (nx < 0) break;
      e = nx;
    }
    const a = polyArea(loop);
    if (a > bestA) { bestA = a; best = loop; }
  }
  return best;
}

/** Douglas-Peucker simplification of an open polyline (keeps both ends). */
export function dp(pts: readonly Pt[], tol: number): Pt[] {
  if (pts.length < 3) return pts.slice();
  let md = 0, mi = 0;
  const a = pts[0] as Pt, b = pts[pts.length - 1] as Pt;
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i] as Pt, d = distSeg(p[0], p[1], a, b);
    if (d > md) { md = d; mi = i; }
  }
  if (md > tol) return dp(pts.slice(0, mi + 1), tol).slice(0, -1).concat(dp(pts.slice(mi), tol));
  return [a, b];
}

/** Simplifies a closed loop, raising the tolerance until it has at most 48 points (the prototype's simplifyLoop). */
export function simplifyLoop(pts: readonly Pt[], tol: number): Pt[] {
  if (pts.length < 4) return pts.slice();
  const p0 = pts[0] as Pt, d0 = (p: Pt) => Math.hypot(p[0] - p0[0], p[1] - p0[1]);
  let far = 0;
  pts.forEach((p, i) => { if (d0(p) > d0(pts[far] as Pt)) far = i; });
  let res: Pt[] = [];
  for (let t = tol, k = 0; k < 8; t *= 1.35, k++) {
    const a = dp(pts.slice(0, far + 1), t), b = dp(pts.slice(far).concat([p0]), t);
    res = a.slice(0, -1).concat(b.slice(0, -1));
    if (res.length <= 48) break;
  }
  return res.length >= 3 ? res : pts.slice();
}

/** Outlines of all regions, as grid-corner keys (key = y*(N+1) + x). */
export interface TraceResult {
  /** Ring of grid-corner keys per label. Keys are shared: a wall's corners are the same keys in both rings. */
  rings: Map<number, number[]>;
  /** Corner keys where three or more regions meet: keep these sharp (pinned). */
  junctions: Set<number>;
}

/** A region's walls stay within this share of its thickness of the cells (see traceRegions), but at least THIN_TOL_MIN cells. */
export const THIN_TOL_SHARE = 0.4;
export const THIN_TOL_MIN = 0.5;

export interface TraceOptions {
  /** Simplification tolerance in cells (the prototype used 1.3). */
  tol: number;
  /** Cells a simplified wall may not cut through even when they are "no region" (the road surface). */
  hard?: Uint8Array;
}

// Directions: 0 east, 1 south, 2 west, 3 north (y grows downwards). The region an edge outlines is on its right.
const DX = [1, 0, -1, 0], DY = [0, 1, 0, -1];
const RIGHT_I = [0, -1, -1, 0], RIGHT_J = [0, 0, -1, -1];
const LEFT_I = [0, 0, -1, -1], LEFT_J = [-1, 0, 0, -1];

/**
 * Outlines every region of `lab` (a label per cell, -1 for none). Each label should be one 4-connected piece
 * with no other label inside its holes (see keepLargestPieces and absorbHoles); only outer outlines are traced.
 * Walls are split at junction corners and simplified once per wall with Douglas-Peucker, with the extra rule
 * that a simplified segment only crosses cells of the two regions on either side, so walls can't cut into a
 * third region.
 */
export function traceRegions(lab: Int32Array, N: number, opts: TraceOptions): TraceResult {
  const V = N + 1, hard = opts.hard;
  const labAt = (i: number, j: number) => (i < 0 || j < 0 || i >= N || j >= N ? -1 : lab[j * N + i] as number);
  const isEdge = (L: number, x: number, y: number, d: number) =>
    labAt(x + (RIGHT_I[d] as number), y + (RIGHT_J[d] as number)) === L && labAt(x + (LEFT_I[d] as number), y + (LEFT_J[d] as number)) !== L;
  const junctionCache = new Map<number, boolean>();
  const isJunction = (x: number, y: number) => {
    const key = y * V + x, hit = junctionCache.get(key);
    if (hit !== undefined) return hit;
    const a = labAt(x - 1, y - 1), b = labAt(x, y - 1), c = labAt(x - 1, y), d = labAt(x, y);
    const s = new Set([a, b, c, d]);
    const res = s.size >= 3 || (s.size === 2 && a === d && b === c);
    junctionCache.set(key, res);
    return res;
  };
  const allowed = (k: number, L: number, M: number) => {
    const l = lab[k] as number;
    return (l === L || l === M) && !(l < 0 && hard && hard[k]);
  };
  const segAllowed = (ax: number, ay: number, bx: number, by: number, L: number, M: number) => {
    const len = Math.hypot(bx - ax, by - ay), steps = Math.max(1, Math.ceil(len * 3));
    for (let s = 0; s < steps; s++) {
      const t = (s + 0.5) / steps, px = ax + (bx - ax) * t, py = ay + (by - ay) * t;
      const i = Math.floor(px), j = Math.floor(py);
      if (i < 0 || j < 0 || i >= N || j >= N) { if (L >= 0 && M >= 0) return false; continue; }
      if (!allowed(j * N + i, L, M)) return false;
    }
    return true;
  };
  const kx = (k: number) => k % V, ky = (k: number) => Math.floor(k / V);

  // a thin region (a strip two or three cells wide) would lose much of its area to a full-tolerance wall, so its walls
  // are simplified at most THIN_TOL_SHARE of its thickness (twice its area over its outline length) off the cells
  const cells = new Map<number, number>(), edges = new Map<number, number>();
  for (let k = 0; k < N * N; k++) {
    const L = lab[k] as number;
    if (L < 0) continue;
    const i = k % N, j = (k - i) / N;
    cells.set(L, (cells.get(L) ?? 0) + 1);
    const e = Number(labAt(i - 1, j) !== L) + Number(labAt(i + 1, j) !== L) + Number(labAt(i, j - 1) !== L) + Number(labAt(i, j + 1) !== L);
    if (e) edges.set(L, (edges.get(L) ?? 0) + e);
  }
  const tolOf = (L: number) => (L < 0 ? opts.tol : Math.min(opts.tol, Math.max(THIN_TOL_MIN, THIN_TOL_SHARE * 2 * (cells.get(L) ?? 0) / (edges.get(L) ?? 1))));

  // Douglas-Peucker over chain[lo..hi] with the region rule; pushes kept keys (excluding hi) into out.
  const simplify = (chain: number[], lo: number, hi: number, L: number, M: number, out: number[], tol: number) => {
    const a = chain[lo] as number, b = chain[hi] as number, ax = kx(a), ay = ky(a), bx = kx(b), by = ky(b);
    let md = 0, mi = -1;
    for (let i = lo + 1; i < hi; i++) {
      const p = chain[i] as number, d = distSeg(kx(p), ky(p), [ax, ay], [bx, by]);
      if (d > md || mi < 0) { md = d; mi = i; }
    }
    if (mi >= 0 && (md > tol || !segAllowed(ax, ay, bx, by, L, M))) {
      simplify(chain, lo, mi, L, M, out, tol);
      simplify(chain, mi, hi, L, M, out, tol);
    } else out.push(a);
  };

  // first cell of every label (row-major): its top edge is on the outer outline
  const starts = new Map<number, number>();
  for (let k = 0; k < N * N; k++) { const L = lab[k] as number; if (L >= 0 && !starts.has(L)) starts.set(L, k); }

  const chainCache = new Map<string, number[]>(), rings = new Map<number, number[]>(), junctions = new Set<number>();
  for (const [L, k0] of starts) {
    const i0 = k0 % N, j0 = (k0 - i0) / N;
    const loop: number[] = [], other: number[] = [];
    let x = i0, y = j0, d = 0;
    for (let guard = 0; guard < 8 * N * N; guard++) {
      loop.push(y * V + x);
      other.push(labAt(x + (LEFT_I[d] as number), y + (LEFT_J[d] as number)));
      x += DX[d] as number; y += DY[d] as number;
      if (x === i0 && y === j0) break;
      const r = (d + 1) % 4, l = (d + 3) % 4;
      d = isEdge(L, x, y, r) ? r : isEdge(L, x, y, d) ? d : l;
    }
    const m = loop.length;
    let J: number[] = [];
    for (let i = 0; i < m; i++) { const kk = loop[i] as number; if (isJunction(kx(kk), ky(kk))) J.push(i); }
    if (!J.length) { let mi = 0; for (let i = 1; i < m; i++) if ((loop[i] as number) < (loop[mi] as number)) mi = i; J = [mi]; }
    const ring: number[] = [];
    for (let a = 0; a < J.length; a++) {
      const s = J[a] as number, e = J[(a + 1) % J.length] as number, ch: number[] = [];
      let i = s;
      do { ch.push(loop[i] as number); i = (i + 1) % m; } while (i !== e);
      ch.push(loop[e] as number);
      const M = other[s] as number, n = ch.length;
      const f0 = ch[0] as number, f1 = ch[1] as number, l0 = ch[n - 1] as number, l1 = ch[n - 2] as number;
      const fwd = f0 < l0 || (f0 === l0 && f1 <= l1);
      const can = fwd ? ch : ch.slice().reverse(), ck = `${can[0]},${can[1]}`;
      let simp = chainCache.get(ck);
      if (!simp) {
        simp = [];
        simplify(can, 0, n - 1, L, M, simp, Math.min(tolOf(L), tolOf(M)));
        simp.push(can[n - 1] as number);
        chainCache.set(ck, simp);
      }
      const ordered = fwd ? simp : simp.slice().reverse();
      for (let q = 0; q < ordered.length - 1; q++) ring.push(ordered[q] as number);
      if (isJunction(kx(f0), ky(f0))) junctions.add(f0);
    }
    const clean = ring.filter((k, i) => k !== ring[(i + ring.length - 1) % ring.length]);
    if (new Set(clean).size >= 3) rings.set(L, clean);
  }
  return { rings, junctions };
}
